import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Package } from "lucide-react";
import api from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useCart } from "@/context/CartContext";
import { useUiTexts } from "@/lib/uiTexts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function OrderDialog({ product, onClose }) {
  const { add } = useCart();
  const [form, setForm] = useState({ size: "", quantity: 1, flocage: false, pseudo: "" });

  const total = ((product.price + (form.flocage ? product.flocage_price : 0)) * form.quantity).toFixed(2);

  const submit = (e) => {
    e.preventDefault();
    add({
      product_id: product.id,
      name: product.name,
      price: product.price,
      flocage_price: product.flocage_price,
      image_url: product.image_url,
      size: form.size,
      quantity: form.quantity,
      flocage: form.flocage,
      pseudo: form.flocage ? form.pseudo : "",
    });
    toast.success("Ajouté au panier");
    onClose();
  };

  const inputCls = "w-full rounded-lg border border-input bg-[#0B132B] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

  return (
    <Dialog open={!!product} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-orange-500/30 bg-[#0B132B] sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold uppercase">Ajouter au panier — {product.name}</DialogTitle>
          <DialogDescription>
            {product.show_stock !== false && `Stock limité : ${product.stock} exemplaire(s) restant(s). `}
            {product.allow_backorder ? "Précommande possible même en rupture — expédition dès le réassort. " : ""}
            Vous pourrez ajouter d'autres articles avant de payer.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-400">Taille</label>
              <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })} required>
                <SelectTrigger data-testid="checkout-size-select" className="bg-[#0B132B]"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {product.sizes.map((s) => {
                    const left = (product.stock_by_size || {})[s];
                    const out = left !== undefined && left <= 0;
                    return <SelectItem key={s} value={s} disabled={out && !product.allow_backorder}>{s}{left !== undefined && product.show_stock !== false ? ` — ${left} restant(s)` : ""}{out && !product.allow_backorder ? " (épuisé)" : ""}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-400">Quantité</label>
              <input type="number" min="1" max={product.allow_backorder ? 20 : product.stock} value={form.quantity} data-testid="checkout-quantity-input"
                onChange={(e) => setForm({ ...form, quantity: Math.max(1, Math.min(product.allow_backorder ? 20 : product.stock, +e.target.value || 1)) })}
                className={inputCls} />
            </div>
          </div>
          <label className="flex items-center gap-3 rounded-lg border border-orange-500/20 bg-orange-500/5 p-3 text-sm">
            <input type="checkbox" checked={form.flocage} data-testid="checkout-flocage-checkbox"
              onChange={(e) => setForm({ ...form, flocage: e.target.checked })}
              className="h-4 w-4 accent-orange-500" />
            Flocage pseudo personnalisé (+{product.flocage_price.toFixed(2)} €)
          </label>
          {form.flocage && (
            <input placeholder="Votre pseudo à floquer" value={form.pseudo} data-testid="checkout-pseudo-input"
              onChange={(e) => setForm({ ...form, pseudo: e.target.value })} className={inputCls} required />
          )}
          <button type="submit" disabled={!form.size} data-testid="checkout-submit-button"
            className="glow-orange rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98] disabled:opacity-50">
            Ajouter au panier — {total} €
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Shop() {
  const texts = useUiTexts();
  const [products, setProducts] = useState([]);
  const [teasers, setTeasers] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/teasers").then((r) => setTeasers(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="shop-page" className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">{texts.shop_label}</p>
      <h1 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">{texts.shop_title}</h1>
      <p className="mt-3 max-w-2xl text-slate-400">
        Stocks limités, flocage personnalisé disponible. Chaque commande soutient directement la structure.
      </p>

      <div className="mt-10 grid gap-8 md:grid-cols-2">
        {products.map((p) => (
          <motion.div key={p.id} data-testid={`product-card-${p.slug || p.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
            className="card-tactical group overflow-hidden">
            <div className="relative overflow-hidden bg-[#060B18]">
              <img src={p.image_url} alt={p.name} className="max-h-[480px] w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" />
              {p.badge && (
                <span className="absolute left-4 top-4 rounded-md bg-orange-500 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-[#060B18]">
                  {p.badge}
                </span>
              )}
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-xl font-bold uppercase tracking-tight sm:text-2xl">
                  <Link to={`/boutique/${p.slug || p.id}`} data-testid={`product-link-${p.slug || p.id}`}
                    className="transition-colors hover:text-orange-400">
                    {p.name}
                  </Link>
                </h2>
                <p className="font-mono2 text-2xl font-bold text-orange-500">{p.price.toFixed(2)} €</p>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.description}</p>
              {p.show_stock !== false && (
                <div className="mt-4 flex items-center gap-2 text-sm">
                  <Package className="h-4 w-4 text-orange-500" />
                  {p.stock > 0 ? (
                    <span data-testid={`product-stock-${p.slug || p.id}`} className={p.stock <= 5 ? "font-bold text-orange-400" : "text-slate-300"}>
                      {p.stock} exemplaire(s) restant(s)
                    </span>
                  ) : p.allow_backorder ? (
                    <span data-testid={`product-stock-${p.slug || p.id}`} className="font-bold text-orange-400">Précommande ouverte — expédié après réassort</span>
                  ) : (
                    <span data-testid={`product-stock-${p.slug || p.id}`} className="font-bold text-red-400">Rupture de stock</span>
                  )}
                </div>
              )}
              {p.show_stock !== false && p.stock_by_size && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid={`product-size-stock-${p.slug || p.id}`}>
                  {p.sizes.map((s) => (
                    <span key={s} className={`rounded border px-2 py-0.5 font-mono2 text-[11px] ${(p.stock_by_size[s] ?? 0) > 0 ? "border-slate-600 text-slate-300" : "border-red-500/40 text-red-400"}`}>
                      {s}: {p.stock_by_size[s] ?? 0}
                    </span>
                  ))}
                </div>
              )}
              <button
                onClick={() => setSelected(p)}
                disabled={p.stock <= 0 && !p.allow_backorder}
                data-testid={`product-buy-button-${p.slug || p.id}`}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ShoppingCart className="h-5 w-5" /> {p.stock <= 0 && p.allow_backorder ? "Précommander" : "Ajouter au panier"}
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {teasers.length > 0 && (
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {teasers.map((t) => (
            <div key={t.id} data-testid={`teaser-${t.id}`}
              className="overflow-hidden rounded-xl border border-dashed border-slate-600 text-center transition-colors hover:border-orange-500/40">
              {t.image_url && (
                <img src={t.image_url} alt={t.name} className="max-h-56 w-full bg-[#060B18] object-contain" />
              )}
              <div className="p-6">
                <p className="font-heading text-xl font-bold uppercase text-slate-400">{t.name}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-orange-500">{t.note || texts.shop_teaser_soon}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <OrderDialog product={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
